import { z } from "zod";
import React, { useEffect, useState, SetStateAction } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import Header from "../../../components/Header";
import IconSelector from "../../../components/IconSelector";
import Input from "../../../components/Input";
import MultiSelect from "../../../components/MultiSelect";
import Button from "../../../components/Button";
import UploadContract from "../../../components/Upload";
import BreadcrumbsPage from "../../../components/Breadcrumbs";

import FileApi from "../../../api/file/file";
import apiCategory from "../../../api/category/apiCategory";
import { ResponseUsers } from "./types";

import icon1 from "../../../assets/icons/main-icon.svg";

type Category = { Id: number; Name: string };

const newChannelCreateSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  responsibleUsers: z.any(),
  file: z
    .any()
    .refine(f => f instanceof File, { message: "Arquivo é obrigatório" })
    .refine(
      f => (f instanceof File && f.type === "application/pdf"),
      { message: "Apenas arquivos PDF são permitidos" }
    ),
  category: z.string().min(1, "Categoria é obrigatória"),
  responsibleTypeUsers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .nonempty("Campo de responsáveis é obrigatório")
    .optional(),
});

type NewChannelsSchema = z.infer<typeof newChannelCreateSchema>;

export default function NewFile() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.state?.isEdit;
  const dataEdit = location.state?.data as any;

  const [users, setUsers] = useState<ResponseUsers[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedIconFile, setSelectedIconFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<NewChannelsSchema>({
    resolver: zodResolver(newChannelCreateSchema),
    defaultValues: {
      name: "",
      responsibleUsers: "",
      file: undefined,
      category: "",
      responsibleTypeUsers: isEdit ? undefined : [],
    },
  });

  const listUsersResponsible = [
    { value: "todos", label: "Todos os Proprietários" },
    { value: "dentroPool", label: "Proprietários dentro do pool" },
    { value: "foraPool", label: "Proprietários fora do pool" },
  ];

  useEffect(() => {
    apiCategory.getListCategory().then(resp => setCategories(resp as Category[])).catch(console.error);
    FileApi.listUsers().then(setUsers).catch(console.error);

    fetch(icon1)
      .then(r => r.blob())
      .then(blob => setSelectedIconFile(new File([blob], "icon-default.png", { type: blob.type })))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (isEdit && dataEdit) {
      setValue("name", dataEdit.Name);
      setValue("category", String(dataEdit.CategoryId));
    }
  }, [isEdit, dataEdit, setValue]);

  async function handleSubmitForm(data: NewChannelsSchema) {
    setIsSubmitting(true);
    const poolTypeMap: Record<string, string> = {
      todos: "1",
      dentroPool: "2",
      foraPool: "3",
    };
    const sel = data.responsibleTypeUsers?.[0]?.value || "todos";
    const poolType = poolTypeMap[sel] ?? "1";

    const networkId = Number(localStorage.getItem("selectedNetwork"));
    const catId = Number(data.category);
    const selectedCategory = categories.find(c => c.Id === catId);
    const nameLower = selectedCategory?.Name.toLowerCase();

    const isBoletos = nameLower === "boletos";
    const isCartas = nameLower === "cartas";
    const isCartasPool = nameLower === "cartas distribuição pool";

    const doPresignAndUpload = async (prefix: string) => {
      const iconKey = `${prefix}/icon_${Date.now()}.png`;
      const fileKey = `${prefix}/file_${Date.now()}.pdf`;
      const iconPresign = await FileApi.sendPresing(networkId, iconKey, 60);
      const filePresign = await FileApi.sendPresing(networkId, fileKey, 60);
      await fetch(iconPresign.url, { method: "PUT", body: selectedIconFile! });
      await fetch(filePresign.url, { method: "PUT", body: data.file! });
      return { iconPath: iconPresign.path, filePath: filePresign.path };
    };

    try {
      if (isEdit) {
        await FileApi.editFile(dataEdit.Id, selectedIconFile, data.name, catId, data.file ? [data.file] : []);
      } else if (isBoletos) {
        const { iconPath, filePath } = await doPresignAndUpload("boletos");
        await FileApi.triggerSplitBilling(networkId, iconPath, data.name, Number(poolType), filePath);
      } else if (isCartas) {
        const { iconPath, filePath } = await doPresignAndUpload("cartas");
        await FileApi.triggerSplitLetter(networkId, iconPath, data.name, Number(poolType), filePath);
      } else if (isCartasPool) {
        const { iconPath, filePath } = await doPresignAndUpload("cartas-distribuicao-pool");
        await FileApi.triggerSplitLetterPoolStatement(
          networkId, iconPath, data.name, Number(poolType), filePath
        );
      } else {
        await FileApi.postNewFile(selectedIconFile, [data.file!], data.name, poolType, data.category);
      }
      navigate("/arquivos");
    } catch (e) {
      console.error("Erro ao cadastrar/editar arquivo", e);
      navigate("/arquivos");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col justify-between h-screen pl-10 pt-12">
      <div>
        <BreadcrumbsPage />
        <Header
          title={isEdit ? "Editar documentos" : "Cadastrar documentos"}
          arrowIcon
        />
        <div className="flex flex-col gap-6 w-full shadow-custom rounded-lg p-6">
          <form
            id="my-form"
            className="flex flex-col gap-6"
            onSubmit={handleSubmit(handleSubmitForm)}
          >
            {!isEdit && (
              <Controller
                name="responsibleTypeUsers"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label="Disponibilizar para"
                    data={listUsersResponsible}
                    onChange={(vals: SetStateAction<never[]>) => field.onChange(vals)}
                    style={{ width: "100%" }}
                    register={field}
                    errors={errors.responsibleTypeUsers}
                    message="Campo obrigatório"
                    file={false}
                    keyField="value"
                    labelField="label"
                  />
                )}
              />
            )}

            <div className="flex gap-4">
              <IconSelector
                onIconSelect={icon => {
                  if (React.isValidElement(icon) && icon.props?.src) {
                    fetch(icon.props.src)
                      .then(r => r.blob())
                      .then(blob => setSelectedIconFile(new File([blob], "icon.png", { type: blob.type })))
                      .catch(console.error);
                  }
                }}
              />

              <Input
                label="Nome"
                type="text"
                placeholder="Digite o nome do documento"
                register={register("name")}
                errors={errors.name}
              />

              <div className="flex flex-col w-full">
                <label className="text-[#404040] font-bold text-base">Categoria</label>
                <select
                  {...register("category")}
                  className="mt-1 rounded p-2 border border-[#E3E7FF] w-full h-10 bg-white"
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(c => (
                    <option key={c.Id} value={c.Id}>{c.Name}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col w-full">
              <p className="font-medium text-[#404040]">Arquivo</p>
              <Controller
                name="file"
                control={control}
                render={({ field }) => (
                  <UploadContract
                    name="file"
                    onUpload={files => field.onChange(files[0])}
                    hasFile={!!field.value}
                    error={errors.file}
                  />
                )}
              />
              {errors.file && (
                <p className="text-red-500 text-sm mt-1">{String(errors.file.message)}</p>
              )}
              <p style={{ color: "#6D7882", fontSize: "14px" }}>Tamanho máximo 10Mb</p>
            </div>
          </form>
        </div>
      </div>

      <div className="flex justify-between mt-auto shadow-custom p-10">
        <Button
          title="Voltar"
          onClick={() => navigate(-1)}
          backGroundColor="#FCFCFA"
          color="#F28E00"
          borderRadius={8}
        />
        <Button
          title={
            isSubmitting
              ? isEdit
                ? "Salvando arquivo..."
                : "Cadastrando arquivo..."
              : isEdit
              ? "Salvar arquivo"
              : "Cadastrar arquivo"
          }
          backGroundColor="#F28E00"
          color="#FAFAFC"
          borderRadius={8}
          type="submit"
          form="my-form"
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
}
